export interface UserLocation {
  lat: number;
  lng: number;
}

export enum GeolocationErrorType {
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  POSITION_UNAVAILABLE = 'POSITION_UNAVAILABLE',
  TIMEOUT = 'TIMEOUT',
  NOT_SUPPORTED = 'NOT_SUPPORTED',
  UNKNOWN = 'UNKNOWN'
}

export class GeolocationError extends Error {
  public readonly errorType: GeolocationErrorType;
  
  constructor(message: string, errorType: GeolocationErrorType) {
    super(message);
    this.name = 'GeolocationError';
    this.errorType = errorType;
  }
}

export class GeolocationService {
  private static readonly DEFAULT_OPTIONS: PositionOptions = {
    enableHighAccuracy: false, // Use WiFi/network location, not just GPS
    timeout: 10000,
    maximumAge: 300000, // 5 minutes
  };

  static async getCurrentLocation(
    options: PositionOptions = this.DEFAULT_OPTIONS
  ): Promise<UserLocation> {
    console.log('[Geolocation] Checking support...', {
      hasNavigator: !!navigator,
      hasGeolocation: !!navigator?.geolocation,
      userAgent: navigator?.userAgent,
      platform: navigator?.platform,
      hasAndroidInterface: !!(window as any).AndroidLocationProvider
    });

    // Detect if running in TWA (standalone mode) - use multiple methods
    const displayStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const displayFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
    const navigatorStandalone = (window.navigator as any).standalone === true;
    const isTWA = displayStandalone || displayFullscreen || navigatorStandalone;
    
    // Check if Chrome (TWA uses Chrome)
    const isChrome = navigator.userAgent.includes('Chrome');
    
    // Check if Android native location provider is available
    const androidProvider = (window as any).AndroidLocationProvider;

    console.log('[Geolocation] Environment detection:', {
      displayStandalone,
      displayFullscreen,
      navigatorStandalone,
      isTWA,
      isChrome,
      hasAndroidProvider: !!androidProvider,
      userAgent: navigator.userAgent
    });

    // If in TWA and Android provider is available, use it to bypass Chrome's broken delegation
    if (isTWA && androidProvider && typeof androidProvider.getCurrentLocation === 'function') {
      console.log('[Geolocation] Using native Android LocationProvider (bypassing Chrome)');
      
      try {
        const resultJson = androidProvider.getCurrentLocation();
        console.log('[Geolocation] Android provider result:', resultJson);
        
        const result = JSON.parse(resultJson);
        if (result.success) {
          console.log('[Geolocation] Android location SUCCESS:', result);
          return { lat: result.lat, lng: result.lng };
        } else {
          throw new GeolocationError(
            result.error || 'Android location failed',
            GeolocationErrorType.POSITION_UNAVAILABLE
          );
        }
      } catch (error) {
        console.error('[Geolocation] Android provider error:', error);
        // Fall through to standard geolocation API
      }
    }

    // Standard browser geolocation API (fallback or non-TWA)
    if (!navigator.geolocation) {
      throw new GeolocationError(
        'Geolocation is not supported by this browser',
        GeolocationErrorType.NOT_SUPPORTED
      );
    }

    const finalOptions = options;

    console.log('[Geolocation] Request from URL:', window.location.href);
    console.log('[Geolocation] Using options:', finalOptions, isTWA ? '(TWA mode)' : '(Browser mode)');

    return new Promise((resolve, reject) => {
      // Use call() to ensure correct 'this' binding
      navigator.geolocation.getCurrentPosition.call(
        navigator.geolocation,
        (position: GeolocationPosition) => {
          console.log('[Geolocation] SUCCESS:', {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
          const { latitude, longitude } = position.coords;
          resolve({ lat: latitude, lng: longitude });
        },
        (error: GeolocationPositionError) => {
          console.error('[Geolocation] ERROR:', {
            code: error.code,
            message: error.message,
            PERMISSION_DENIED: error.PERMISSION_DENIED,
            POSITION_UNAVAILABLE: error.POSITION_UNAVAILABLE,
            TIMEOUT: error.TIMEOUT,
          });
          
          let errorType: GeolocationErrorType;
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorType = GeolocationErrorType.PERMISSION_DENIED;
              break;
            case error.POSITION_UNAVAILABLE:
              errorType = GeolocationErrorType.POSITION_UNAVAILABLE;
              break;
            case error.TIMEOUT:
              errorType = GeolocationErrorType.TIMEOUT;
              break;
            default:
              errorType = GeolocationErrorType.UNKNOWN;
              break;
          }
          // Pass the error type, message will be translated in the component
          reject(new GeolocationError(error.message, errorType));
        },
        finalOptions
      );
    });
  }
}
